import { Pencil, Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Link1 from "@stackje/shared-components/cards/link/Link1";
import Link2 from "@stackje/shared-components/cards/link/Link2";
import Link3 from "@stackje/shared-components/cards/link/Link3";
import Link4 from "@stackje/shared-components/cards/link/Link4";
import Link5 from "@stackje/shared-components/cards/link/Link5";
import Link6 from "@stackje/shared-components/cards/link/Link6";
import { Button } from "@stackje/shared-components/ui/button";
import { ClearableInput } from "@stackje/shared-components/ui/clearable-fields";
import { SimpleMarkdownEditor } from "@stackje/shared-components/ui/simple-markdown-editor";
import {
  Dialog,
  DialogTrigger,
} from "@stackje/shared-components/ui/dialog";
import { Label } from "@stackje/shared-components/ui/label";
import { getFallbackLinkText } from "@stackje/shared-components/lib/link-utils";
import { type CommonColor } from "@stackje/common/config";
import { LinkEditFormDialogContent } from "./link-edit-form-dialog-content";
import { useColorSchemeShuffler } from "../../../hooks/useColorSchemeShuffler";
import { sampleData } from "@/components/add-card/constants";

// Zod schema for validation
const linkCardFormSchema = z.object({
  title: z.string(),
  description: z.string(),
  links: z.array(z.object({
    title: z.string().optional(),
    url: z.string().regex(/^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/, "Please enter a valid URL (e.g., https://example.com)"),
    description: z.string().optional(),
    image: z.object({
      src: z.string(),
      alt: z.string().optional(),
    }).optional(),
  })),
  type: z.string(),
  colors: z.object({
    background: z.string(),
    foreground: z.string(),
    cardBackground: z.string(),
    cardForeground: z.string(),
    buttonBackground: z.string(),
  }),
});

export type LinkCardFormData = z.infer<typeof linkCardFormSchema>;

interface LinkCardFormProps {
  initialData: LinkCardFormData;
  mode: "add" | "edit";
  onTypeChange?: (type: string) => void;
}

export function useLinkCardForm({
  initialData,
  mode,
  onTypeChange,
}: LinkCardFormProps) {
  const form = useForm<LinkCardFormData>({
    resolver: zodResolver(linkCardFormSchema),
    defaultValues: initialData,
  });

  const { fields, append, update, remove } = useFieldArray({
    control: form.control,
    name: "links",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLinkIndex, setEditingLinkIndex] = useState<number | null>(null);
  const [editingLinkData, setEditingLinkData] = useState<{
    title?: string;
    url: string;
    description?: string;
    image?: { src: string; alt?: string };
  } | undefined>(undefined);
  const { colors, setColors, shuffleColors } = useColorSchemeShuffler(initialData.colors as CommonColor)

  // Watch form values for preview updates
  const watchedValues = form.watch();
  const title = watchedValues.title;
  const description = watchedValues.description;
  const links = watchedValues.links;
  const linkType = watchedValues.type;
  const isDirty = form.formState.isDirty;

  useEffect(() => {
    setColors(initialData.colors as CommonColor);
    form.setValue("colors", initialData.colors);
  }, [initialData.colors, setColors, form]);

  // Update form colors when shuffled
  useEffect(() => {
    form.setValue("colors", colors);
  }, [colors, form]);
  
  const toggleType = (newType?: string) => {
    if (newType) {
      form.setValue("type", newType);
      onTypeChange?.(newType);
      return;
    }

    const linkTypes = ["link1", "link2", "link3", "link4", "link5", "link6"];
    const currentIndex = linkTypes.indexOf(linkType);
    const nextIndex = (currentIndex + 1) % linkTypes.length;
    const nextType = linkTypes[nextIndex];
    form.setValue("type", nextType);
    onTypeChange?.(nextType);
  };

  const handleLinkSubmit = (data: {
    title?: string;
    url: string;
    description?: string;
    image?: { src: string; alt?: string };
  }) => {
    if (editingLinkIndex !== null) {
      // Edit existing link
      update(editingLinkIndex, data);
    } else {
      // Add new link
      append(data);
    }
    // Reset form
    setEditingLinkIndex(null);
    setEditingLinkData(undefined);
    setDialogOpen(false);
  };

  const handleOpenChangeDialog = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingLinkIndex(null);
      setEditingLinkData(undefined);
    }
  };

  const formContent = (
    <>
      <div className="space-y-2" data-testid="link-form-content">
        <div className="py-2">
          <h2 className="text-lg font-semibold">Links Card</h2>
          <p className="text-sm text-muted-foreground">
            Not all fields will be used for a given card type.
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="text-red-500">*</span> indicates required fields
          </p>
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="title">Title</Label>
          <Controller
            name="title"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="title"
                value={field.value}
                onChange={field.onChange}
                placeholder="Enter title"
              />
            )}
          />
          <div className="my-2">
            <Label htmlFor="description">Description</Label>
            <Controller
              name="description"
              control={form.control}
              render={({ field }) => (
                <SimpleMarkdownEditor
                  id="description"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter description"
                />
              )}
            />
          </div>
        </div>

        <div className="border-t pb-2 pt-4">
          <h3 className="mb-2">Links <span className="text-red-500">*</span></h3>
          {fields.map((field, index) => (
            <div
              key={index}
              className="flex items-center gap-4 p-4 border rounded-lg mb-3 bg-muted/30"
            >
              {/* Image preview on the left */}
              <div className="flex-shrink-0">
                {field.image?.src ? (
                  <img
                    src={field.image.src}
                    alt={field.image.alt || field.title || "Link image"}
                    className="w-16 h-16 object-cover rounded border bg-muted"
                  />
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center border border-dashed border-muted-foreground/25 rounded bg-muted text-xs text-muted-foreground">
                    No image
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h4 className="font-medium">{getFallbackLinkText(field)}</h4>
                <p className="text-sm text-muted-foreground truncate">
                  {field.url}
                </p>
                {field.description && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {field.description}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Edit link"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditingLinkIndex(index);
                    setEditingLinkData({
                      title: field.title,
                      url: field.url,
                      description: field.description,
                      image: field.image,
                    });
                    setDialogOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Delete link"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    remove(index);
                  }}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          <div className="">
            <Dialog open={dialogOpen} onOpenChange={handleOpenChangeDialog}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setEditingLinkIndex(null);
                    setEditingLinkData(undefined);
                  }}
                >
                  Add a link
                </Button>
              </DialogTrigger>
              <LinkEditFormDialogContent
                key={`link-dialog-${editingLinkIndex ?? 'new'}`}
                isEditing={editingLinkIndex !== null}
                initialData={editingLinkData}
                onSubmit={handleLinkSubmit}
                editingLinkIndex={editingLinkIndex}
                linksLength={fields.length}
                onClose={() => setDialogOpen(false)}
              />
            </Dialog>
          </div>
        </div>

        {/* Hidden inputs */}
        <input type="hidden" name="type" value={linkType} />
        <input type="hidden" name="colors" value={JSON.stringify(colors)} />
        {fields.map((field, index) => (
          <div key={field.id}>
            <input
              type="hidden"
              name={`linkTitle${index}`}
              value={field.title || ""}
            />
            <input type="hidden" name={`linkUrl${index}`} value={field.url} />
            <input
              type="hidden"
              name={`linkDescription${index}`}
              value={field.description || ""}
            />
            <input
              type="hidden"
              name={`linkImageSrc${index}`}
              value={field.image?.src || ""}
            />
            <input
              type="hidden"
              name={`linkImageAlt${index}`}
              value={field.image?.alt || ""}
            />
          </div>
        ))}
      </div>
    </>
  );

  return {
    form,
    title,
    description,
    links,
    linkType,
    toggleType,
    shuffleColors,
    isDirty,
    formContent,
    handleSubmit: form.handleSubmit,
    previewContent: (() => {
      if (isDirty || mode === "edit") {
        const commonProps = {
          title,
          description,
          links,
          colors,
        };

        switch (linkType) {
          case "link1":
            return <Link1 {...commonProps} />;
          case "link2":
            return <Link2 {...commonProps} />;
          case "link3":
            return <Link3 {...commonProps} />;
          case "link4":
            return <Link4 {...commonProps} />;
          case "link5":
            return <Link5 {...commonProps} />;
          case "link6":
            return <Link6 {...commonProps} />;
          default:
            return <Link1 {...commonProps} />;
        }
      } else {
        const sampleProps = {
          title: sampleData.links.title,
          description: sampleData.links.description,
          links: sampleData.links.links,
          colors,
        };

        switch (linkType) {
          case "link1":
            return <Link1 {...sampleProps} />;
          case "link2":
            return <Link2 {...sampleProps} />;
          case "link3":
            return <Link3 {...sampleProps} />;
          case "link4":
            return <Link4 {...sampleProps} />;
          case "link5":
            return <Link5 {...sampleProps} />;
          case "link6":
            return <Link6 {...sampleProps} />;
          default:
            return <Link1 {...sampleProps} />;
        }
      }
    })(),
  };
}
